/**
 * <mlp-video> — lazy, performance-first hover-to-play wrapper for muted looping clips.
 *
 * Perf model:
 *  - The inner <video> ships with preload="none" and no poster attribute, so NOTHING
 *    downloads until the clip is asked to play. The visible still is a separate
 *    native-lazy <img class="mlp-media__poster">, which only loads near the viewport.
 *  - Desktop (hover-capable): plays on mouseenter, pauses + rewinds on mouseleave.
 *  - Touch / no-hover: plays only while >=60% in view (IntersectionObserver), so a
 *    grid of many clips never loads more than the few on screen.
 *  - autoplay="true": always uses the in-view observer regardless of input type.
 *  - Honors prefers-reduced-motion (no autoplay; tap/hover still works).
 *  - Adds .is-playing on the host once frames are actually rendering, so the poster
 *    fades out only when there's real video underneath (no black flash).
 */
if (!customElements.get('mlp-video')) {
  const REDUCED = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const CAN_HOVER = window.matchMedia('(hover: hover) and (pointer: fine)').matches;

  class MlpVideo extends HTMLElement {
    connectedCallback() {
      this.video = this.querySelector('video');
      if (!this.video) return;

      this.video.muted = true;
      this.video.loop = true;
      this.video.playsInline = true;
      this.video.setAttribute('playsinline', '');
      this.video.preload = 'none';

      this.autoplay = this.getAttribute('data-autoplay') === 'true';

      this.video.addEventListener('playing', () => this.classList.add('is-playing'));
      this.video.addEventListener('pause', () => this.classList.remove('is-playing'));

      if ((this.autoplay || !CAN_HOVER) && !REDUCED) {
        this._observe();
      }

      if (CAN_HOVER && !this.autoplay) {
        this._onEnter = () => this._play();
        this._onLeave = () => this._pause(true);
        this.addEventListener('mouseenter', this._onEnter);
        this.addEventListener('mouseleave', this._onLeave);
        this.addEventListener('focusin', this._onEnter);
        this.addEventListener('focusout', this._onLeave);
      }

      // Touch tap toggles play — but only when the tile is not itself a link
      // (otherwise a tap should follow the link, not hijack it).
      if (!CAN_HOVER && !this.querySelector('a')) {
        this._onTap = () => (this.video.paused ? this._play() : this._pause(false));
        this.addEventListener('click', this._onTap);
      }
    }

    _observe() {
      this._io = new IntersectionObserver(
        (entries) => {
          entries.forEach((e) => (e.isIntersecting ? this._play() : this._pause(false)));
        },
        { threshold: 0.6 }
      );
      this._io.observe(this);
    }

    _play() {
      const p = this.video.play();
      if (p && typeof p.catch === 'function') p.catch(() => {});
    }

    _pause(rewind) {
      try {
        this.video.pause();
        if (rewind) this.video.currentTime = 0;
      } catch (e) {}
    }

    disconnectedCallback() {
      if (this._io) this._io.disconnect();
    }
  }

  customElements.define('mlp-video', MlpVideo);
}

/**
 * Testimonial style tabs — filter the rail to the active style.
 * Cards carry data-style (handleized); tabs carry the same. Style-less cards
 * always show. Runs on load (first tab active) and on tab click.
 */
if (!window.__mlpTabsBound) {
  window.__mlpTabsBound = true;

  const applyTabs = (tabsEl) => {
    const wrap = tabsEl.closest('.mlp-vt');
    const rail = wrap && wrap.querySelector('.mlp-vt__rail');
    if (!rail) return;
    let active = tabsEl.querySelector('.mlp-vt__tab.is-active') || tabsEl.querySelector('.mlp-vt__tab');
    if (!active) return;
    active.classList.add('is-active');
    const style = active.getAttribute('data-style');
    rail.querySelectorAll('.mlp-vt__card').forEach((c) => {
      const cs = c.getAttribute('data-style');
      c.classList.toggle('is-filtered-out', !!cs && cs !== style);
    });
    rail.scrollLeft = 0;
  };

  document.addEventListener('click', (e) => {
    const tab = e.target.closest('.mlp-vt__tab');
    if (!tab) return;
    const tabsEl = tab.closest('.mlp-vt__tabs');
    tabsEl.querySelectorAll('.mlp-vt__tab').forEach((t) => t.classList.remove('is-active'));
    tab.classList.add('is-active');
    applyTabs(tabsEl);
  });

  const initTabs = () => document.querySelectorAll('.mlp-vt__tabs').forEach(applyTabs);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', initTabs);
  else initTabs();
}

/**
 * "Load more" reveal — un-hides the next batch of grid tiles that were rendered
 * with .is-hidden (display:none). Their lazy posters only fetch once revealed and
 * near the viewport, so the initial payload stays small no matter how many clips exist.
 */
if (!window.__mlpLoadMoreBound) {
  window.__mlpLoadMoreBound = true;
  document.addEventListener('click', (e) => {
    const btn = e.target.closest('[data-mlp-loadmore]');
    if (!btn) return;
    const wrap = btn.closest('.mlp-wg__more');
    const grid = wrap && wrap.previousElementSibling;
    if (!grid) return;
    const batch = parseInt(btn.getAttribute('data-batch'), 10) || 8;
    const hidden = grid.querySelectorAll('.is-hidden');
    for (let i = 0; i < batch && i < hidden.length; i++) hidden[i].classList.remove('is-hidden');
    if (grid.querySelectorAll('.is-hidden').length === 0) wrap.style.display = 'none';
  });
}
